// TraceFull v7 — Retailers Page
import type { SharedPageProps } from '../types';
import { useState } from 'react';
import { T, SectionHeader, Chip, Card, ScoreRing, RISK_COLOR } from '../components/ui';
import type { Retailer } from '../api/client';


const RETAILER_META: Record<string, { emoji: string; tier: string; region: string }> = {
 'whole-foods': { emoji: '🛒', tier: 'enterprise', region: 'North America' },
 'target': { emoji: '🎯', tier: 'growth', region: 'North America' },
 'kroger': { emoji: '🏪', tier: 'growth', region: 'North America' },
 'walmart': { emoji: '🏬', tier: 'enterprise', region: 'Global' },
 'costco': { emoji: '📦', tier: 'growth', region: 'North America' },
 'safeway': { emoji: '🥗', tier: 'starter', region: 'West Coast' },
};

const TIER_C: Record<string, string> = { enterprise: T.copper, growth: T.sapphire, starter: T.sage };

export default function RetailersPage({ products, retailers, alerts }: SharedPageProps) {
 const [selected, setSelected] = useState<Retailer | null>(null);
 const [search, setSearch] = useState('');

 const filtered = retailers.filter(r =>
 !search || r.name.toLowerCase().includes(search.toLowerCase())
 );

 // Products for selected retailer
 const retailerProducts = selected
 ? products.filter(p => p.retailer_id === selected.id || p.retailer_slug === selected.slug)
 : [];

 const retailerAlerts = selected
 ? alerts.filter(a => retailerProducts.some(p => p.id === a.product_id))
 : [];

 // Aggregate score for a retailer
 const avgScore = (r: Retailer) => {
 const ps = products.filter(p => p.retailer_id === r.id || p.retailer_slug === r.slug);
 if (!ps.length) return 0;
 return Math.round(ps.reduce((s, p) => s + (p.provenance_score || 0), 0) / ps.length);
 };

 const riskCounts = (r: Retailer) => {
 const ps = products.filter(p => p.retailer_id === r.id || p.retailer_slug === r.slug);
 return { LOW: ps.filter(p => p.risk_level === 'LOW').length, HIGH: ps.filter(p => p.risk_level === 'HIGH' || p.risk_level === 'CRITICAL').length, total: ps.length };
 };

 return (
 <div className="fade-up">
 <SectionHeader
 title="Retailer Network"
 sub={`${retailers.length} retailers · manage product assignments and view compliance status`}
 right={
 <button style={{ padding: '8px 16px', background: T.copper + '22', border: `1px solid ${T.copper}`,
 color: T.copper, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
 + Add Retailer
 </button>
 }
 />

 <div style={{ display: 'grid', gridTemplateColumns: selected ? '320px 1fr' : '1fr', gap: 18 }}>

 {/* Retailer list */}
 <div>
 <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search retailers…"
 style={{ width: '100%', background: T.panel, border: `1px solid ${T.border}`, borderRadius: 9,
 padding: '9px 14px', color: T.text, fontSize: 12, outline: 'none',
 fontFamily: "'DM Sans',sans-serif", marginBottom: 12 }} />

 {filtered.length === 0 && (
 <div style={{ textAlign: 'center', padding: '40px 0', color: T.sub, fontSize: 12 }}>No retailers found</div>
 )}

 <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
 {filtered.map(r => {
 const meta = RETAILER_META[r.slug] || { emoji: '🏪', tier: 'starter', region: 'Unknown' };
 const score = avgScore(r);
 const rc = riskCounts(r);
 const rProds = products.filter(p => p.retailer_id === r.id || p.retailer_slug === r.slug);
 const openA = alerts.filter(a => rProds.some(p => p.id === a.product_id) && a.status === 'open').length;
 return (
 <div key={r.id} onClick={() => setSelected(selected?.id === r.id ? null : r)}
 style={{ background: selected?.id === r.id ? T.lift : T.panel,
 border: `1px solid ${selected?.id === r.id ? T.copper + '60' : T.border}`,
 borderRadius: 12, padding: 16, cursor: 'pointer', transition: 'all .1s' }}>
 <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
 <div style={{ fontSize: 32, flexShrink: 0 }}>{meta.emoji}</div>
 <div style={{ flex: 1 }}>
 <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 4 }}>{r.name}</div>
 <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
 <Chip label={meta.tier} color={TIER_C[meta.tier]} />
 <Chip label={meta.region} color={T.sub} />
 {openA > 0 && <Chip label={`${openA} alerts`} color={T.crimson} dot />}
 </div>
 <div style={{ display: 'flex', gap: 16, fontSize: 11, color: T.sub }}>
 <span>{rc.total} products</span>
 <span style={{ color: T.crimson }}>{rc.HIGH} high risk</span>
 </div>
 </div>
 <ScoreRing score={score} size={48} />
 </div>
 </div>
 );
 })}
 </div>
 </div>

 {/* Retailer detail */}
 {selected && (() => {
 const meta = RETAILER_META[selected.slug] || { emoji: '🏪', tier: 'starter', region: 'Unknown' };
 const score = avgScore(selected);
 return (
 <div>
 <Card style={{ marginBottom: 14 }}>
 <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
 <div style={{ fontSize: 52 }}>{meta.emoji}</div>
 <div style={{ flex: 1 }}>
 <div style={{ fontFamily: "'Fraunces',serif", fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 4 }}>{selected.name}</div>
 <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
 <Chip label={meta.tier} color={TIER_C[meta.tier]} />
 <Chip label={meta.region} color={T.sub} />
 <Chip label={selected.slug} color={T.dim} />
 </div>
 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
 {[
 { label: 'Products', value: retailerProducts.length, color: T.copper },
 { label: 'Avg Score', value: score, color: score >= 70 ? T.sage : T.amber },
 { label: 'Open Alerts', value: retailerAlerts.filter(a => a.status === 'open').length, color: T.crimson },
 { label: 'Verified', value: retailerProducts.filter(p => p.verified).length, color: T.sage },
 ].map(s => (
 <div key={s.label} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 14px', textAlign: 'center' }}>
 <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
 <div style={{ fontSize: 10, color: T.sub, textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 3 }}>{s.label}</div>
 </div>
 ))}
 </div>
 </div>
 <ScoreRing score={score} size={72} />
 </div>
 </Card>

 <Card>
 <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 14 }}>Assigned Products ({retailerProducts.length})</div>
 {retailerProducts.length === 0 ? (
 <div style={{ color: T.sub, fontSize: 12, textAlign: 'center', padding: '20px 0' }}>No products assigned</div>
 ) : (
 <table style={{ width: '100%', borderCollapse: 'collapse' }}>
 <thead>
 <tr>{['Product','Brand','Score','Risk','Blockchain'].map(h => (
 <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, color: T.sub,
 textTransform: 'uppercase', letterSpacing: 0.6, borderBottom: `1px solid ${T.border}` }}>{h}</th>
 ))}</tr>
 </thead>
 <tbody>
 {retailerProducts.map(p => (
 <tr key={p.id}>
 <td style={{ padding: '11px 12px', fontSize: 13, color: T.text, borderBottom: `1px solid ${T.border}` }}>
 <span style={{ marginRight: 8 }}>{p.img_emoji}</span>{p.name}
 </td>
 <td style={{ padding: '11px 12px', fontSize: 12, color: T.sub, borderBottom: `1px solid ${T.border}` }}>{p.brand}</td>
 <td style={{ padding: '11px 12px', borderBottom: `1px solid ${T.border}` }}><ScoreRing score={p.provenance_score} size={32} /></td>
 <td style={{ padding: '11px 12px', borderBottom: `1px solid ${T.border}` }}><Chip label={p.risk_level || 'LOW'} color={RISK_COLOR[p.risk_level] || T.sage} /></td>
 <td style={{ padding: '11px 12px', borderBottom: `1px solid ${T.border}` }}>
 {p.blockchain_hash ? <Chip label="On-chain" color={T.sapphire} dot /> : <span style={{ color: T.dim, fontSize: 11 }}>—</span>}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 )}
 </Card>
 </div>
 );
 })()}
 </div>
 </div>
 );
}
